import mobile from "is-mobile";
import type {FC, ReactElement} from "react";
import type React from "react";
import {memo, useCallback, useRef} from "react";
import {type NativeSyntheticEvent, type NativeTouchEvent, Platform} from "react-native";

import {isReactElement, useTimeout} from "../../react";
import type {IButtonProps} from "../ui/button";
import {Button, ButtonIcon, ButtonSpinner} from "../ui/button";
import type {IIConProps} from "../ui/icon";
import type {TooltipRef} from "../ui/tooltip";
import {Tooltip} from "../ui/tooltip";

type Props = {
    testID?: string;
    icon:
        | React.ComponentType<{size?: IIConProps["size"]; color?: IIConProps["color"]}>
        | ReactElement<IIConProps>;
    tooltip: React.ReactNode;
    isLoading?: boolean;
    placement?: "left" | "right";
    isDisabled?: boolean;
    size?: IButtonProps["size"];
    className?: string;
    action?: IButtonProps["action"];
    onPress?: IButtonProps["onPress"];
};

const ButtonIconTooltip: FC<Props> = ({
    testID,
    icon: Icon,
    isLoading,
    tooltip,
    size = "md",
    placement,
    isDisabled,
    className,
    action = "default",
    onPress
}) => {
    const iconNode = isReactElement(Icon) ? (
        Icon
    ) : (
        <ButtonIcon as={Icon} className={isDisabled ? "fill-typography-100" : undefined} />
    );
    const ref = useRef<TooltipRef>(null);
    const closeTooltipTimeout = useTimeout(() => {
        ref.current?.closeTooltip();
    }, 1_000);

    const handlePress = useCallback(
        (e: NativeSyntheticEvent<NativeTouchEvent>) => {
            if (Platform.OS !== "web" || mobile({tablet: true, featureDetect: true})) {
                ref.current?.openTooltip();
                closeTooltipTimeout();
            }
            onPress?.(e);
        },
        [closeTooltipTimeout, onPress]
    );

    const btnIcon = (
        <Button
            action={action}
            aria-label={typeof tooltip === "string" ? tooltip : undefined}
            className={className}
            isDisabled={isDisabled}
            size={size}
            testID={testID}
            variant="icon"
            onPress={handlePress}
        >
            {isLoading ? <ButtonSpinner testID="spinner" /> : iconNode}
        </Button>
    );

    // tooltip doesn't work great on app so temporarily disable it
    if (process.env.IS_WEB) {
        return (
            <Tooltip ref={ref} label={tooltip} placement={placement}>
                {btnIcon}
            </Tooltip>
        );
    }

    return btnIcon;
};

export default memo(ButtonIconTooltip);
